import { Suspense, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { getMonthNameByDateObject, getWeekdayByDate } from '@/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import DatePicker from './DatePicker';
import DatePickerFallback from './DatePickerFallback';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';

// eslint-disable-next-line max-lines-per-function
export default function DatePickerWrapper({
  date,
  onSelect,
  onForward,
  onBackward,
}: {
  date: Date;
  onSelect: (date: Date) => void;
  onForward: (date: Date) => void;
  onBackward: (date: Date) => void;
}) {
  const [currentMonth, setCurrentMonth] = useState(date);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const handleSelect = (d: Date) => {
    setOpen(false); // Close the popover first
    setTimeout(() => onSelect(d), 0); // Navigate after the popover is closed
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="cursor-pointer"
          onClick={() => {
            const previousDay = date;
            date.setDate(date.getDate() - 1);
            onBackward(previousDay);
          }}>
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="text-neutrals-700 text-xl"
          />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="cursor-pointer"
          onClick={() => {
            const nextDay = date;
            date.setDate(date.getDate() + 1);
            onForward(nextDay);
          }}>
          <FontAwesomeIcon
            icon={faChevronRight}
            className="text-neutrals-700 text-xl"
          />
        </Button>

        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className={'justify-start text-left text-2xl font-bold'}>
            {`${
              getWeekdayByDate(date).shortage
            }., ${date.getDate()}. ${getMonthNameByDateObject(date)}`}
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <Suspense
          fallback={
            <DatePickerFallback
              currentMonth={currentMonth}
              date={date}
              onSelect={onSelect}
              setCurrentMonth={setCurrentMonth}
            />
          }>
          <DatePicker
            date={date}
            onSelect={handleSelect}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
          />
        </Suspense>
        <div className="pb-3 px-3">
          <Button
            className="w-full"
            variant="ghost"
            onClick={() => {
              handleSelect(new Date());
            }}>
            {t('general.date.today')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
